import { useContext, useEffect, useMemo } from 'react'
import { withRouter } from 'react-router-dom'
import * as H from 'history'
import { ContentBody, ContentHeader } from 'components/shared/content'
import { useGroupApi } from 'api/group_by'
import { useForm } from 'utils/hooks'
import { PcrCtSearchForm } from 'entities/pcr_ct'
import moment from 'moment'
import { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { Flex } from 'components/shared/flex'
import { MultipleSelectField, SelectField } from 'components/shared/input'
import { Accordion, Button, ButtonGroup, Dropdown, DropdownButton } from 'react-bootstrap'
import { QcType, QC_TYPE } from 'entities/protocol_version'
import { useFetchProductItemsApi } from 'api/product_item'
import { GlobalStateContext } from 'contexts/global_state_context'
import { ProtocolVersionViewModel } from 'view_models/protocol_version_view_model'
import { ProductItemSearchForm } from 'entities/product_item'
import { ModalButton } from 'components/shared/widget'
import { QcTypeBadge } from 'view_models/result_view_model'
import { selectYears } from 'utils/util'

const CtChart = ({
    probeName,
    qcType,
    ucl,
    lcl,
    rucl,
    ctChartSearchForm,
    dnaCode,
}: {
    probeName: string
    qcType?: QcType
    ucl?: number
    lcl?: number
    rucl?: number
    ctChartSearchForm: CtChartSearchForm
    dnaCode?: string
}): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const lastDay = useMemo(() => {
        return ('0' + new Date(Number(ctChartSearchForm.year), Number(ctChartSearchForm.month), 0).getDate()).slice(-2)
    }, [ctChartSearchForm])
    const searchForm = useForm<PcrCtSearchForm>({
        groupBy: 'day',
        aggregate: 'Avg("ct_ave")',
        truncate: 'day',
        truncDateColumn: 'create_date',
        truncDateColumnType: 'date',
        createDateAfter: `${ctChartSearchForm.year}-${ctChartSearchForm.month}-01`,
        createDateBefore: `${ctChartSearchForm.year}-${ctChartSearchForm.month}-${lastDay}`,
        pcrTarget: probeName,
        dnaCode: dnaCode,
    })
    const maxSearchForm = useForm<PcrCtSearchForm>({
        groupBy: 'day',
        aggregate: 'Max("ct_ave")',
        truncate: 'day',
        truncDateColumn: 'create_date',
        truncDateColumnType: 'date',
        createDateAfter: `${ctChartSearchForm.year}-${ctChartSearchForm.month}-01`,
        createDateBefore: `${ctChartSearchForm.year}-${ctChartSearchForm.month}-${lastDay}`,
        pcrTarget: probeName,
        dnaCode: dnaCode,
    })
    const minSearchForm = useForm<PcrCtSearchForm>({
        groupBy: 'day',
        aggregate: 'Min("ct_ave")',
        truncate: 'day',
        truncDateColumn: 'create_date',
        truncDateColumnType: 'date',
        createDateAfter: `${ctChartSearchForm.year}-${ctChartSearchForm.month}-01`,
        createDateBefore: `${ctChartSearchForm.year}-${ctChartSearchForm.month}-${lastDay}`,
        pcrTarget: probeName,
        dnaCode: dnaCode,
    })

    // useEffect(() => {
    //     avgApi.execute()
    //     maxApi.execute()
    //     minApi.execute()
    // }, [])

    useEffect(() => {
        const forms = [searchForm, maxSearchForm, minSearchForm]
        forms.forEach((f) => {
            f.update((f) => {
                f.pcrTarget = probeName
                f.dnaCode = dnaCode
                f.createDateAfter = `${ctChartSearchForm.year}-${ctChartSearchForm.month}-01`
                f.createDateBefore = `${ctChartSearchForm.year}-${ctChartSearchForm.month}-${lastDay}`
            })
        })
    }, [probeName, dnaCode, ctChartSearchForm])

    const avgApi = useGroupApi('pcr_cts', searchForm)
    const maxApi = useGroupApi('pcr_cts', maxSearchForm)
    const minApi = useGroupApi('pcr_cts', minSearchForm)

    const xData = useMemo(() => {
        if (avgApi.isSuccess()) {
            return avgApi.response.groups
                .sort((a, b) => moment(a.day).unix() - moment(b.day).unix())
                .map((d) => {
                    return { day: d.day, avg: d.agr }
                })
        } else {
            return []
        }
    }, [avgApi.loading])

    const rData = useMemo(() => {
        if (maxApi.isSuccess() && minApi.isSuccess()) {
            return maxApi.response.groups
                .sort((a, b) => moment(a.day).unix() - moment(b.day).unix())
                .map((max) => {
                    const min = minApi.response.groups.find((i) => i.day === max.day)
                    const r = max.agr - (min?.agr ?? 0)
                    return { day: moment(max.day).format('M/D'), r: r, ucl: rucl }
                })
        } else {
            return []
        }
    }, [maxApi.loading, minApi.loading])

    return (
        <div style={{ width: '100%', padding: 5 }}>
            <div className="shadow" style={{ width: 'calc(100% - 20px)' }}>
                <Flex alignItems="center" style={{ marginTop: 10 }}>
                    <div style={{ marginLeft: 10, color: '#666' }}>{probeName}</div>
                    <QcTypeBadge qcType={qcType} />
                </Flex>
                <div style={{ marginTop: 10, fontSize: 12, width: '100%', textAlign: 'center' }}>X管理図</div>
                <ResponsiveContainer width="100%" height={120}>
                    <LineChart
                        width={500}
                        height={120}
                        data={xData}
                        margin={{
                            top: 20,
                            right: 50,
                            left: 20,
                            bottom: 5,
                        }}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="day" angle={-25} textAnchor="end" height={0} tick={{ fontSize: 14 }} />
                        <YAxis
                            width={20}
                            domain={lcl !== undefined && ucl !== undefined ? [lcl, ucl] : undefined}
                            tickFormatter={(v) => String(Math.round((v as number) * 10) / 10)}
                        />
                        <Tooltip />
                        <Line type="monotone" dataKey="avg" stroke="#29B6F6" />
                        <ReferenceLine y={ucl} label="UCL" stroke="#F44336" strokeWidth={2} />
                        <ReferenceLine y={lcl} label="LCL" stroke="#4CAF50" strokeWidth={2} />
                    </LineChart>
                </ResponsiveContainer>
                <div style={{ marginTop: 10, fontSize: 12, width: '100%', textAlign: 'center' }}>R管理図</div>
                <ResponsiveContainer width="100%" height={170}>
                    <LineChart
                        width={500}
                        height={170}
                        data={rData}
                        margin={{
                            top: 20,
                            right: 50,
                            left: 20,
                            bottom: 5,
                        }}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="day" angle={-25} textAnchor="end" height={60} tick={{ fontSize: 14 }} />
                        <YAxis
                            width={20}
                            domain={rucl !== undefined ? [-1, rucl] : undefined}
                            tickFormatter={(v) => String(Math.round((v as number) * 10) / 10)}
                        />
                        <Tooltip />
                        <ReferenceLine y={rucl} label="UCL" stroke="#F44336" strokeWidth={2} />
                        <Line type="monotone" dataKey="r" stroke="#29B6F6" />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    )
}

type Props = {
    history: H.History
}

export type CtChartSearchForm = {
    year: string
    month: string
}

const CtChartPage = (props: Props) => {
    const globalState = useContext(GlobalStateContext)

    const protocolVersionViewModle = new ProtocolVersionViewModel({})

    const searchForm = useForm<ProductItemSearchForm>({ scope: 'only_qc' })
    const productItemApi = useFetchProductItemsApi(searchForm)

    type VersionNumber = { rnaStandard: number; qc: number }
    const versionNumberForm = useForm<VersionNumber>({
        rnaStandard: 0,
        qc: 0,
    })

    const form = useForm<CtChartSearchForm>({ year: moment().format('YYYY'), month: moment().format('MM') })
    type DisplaySettingForm = {
        standardQc?: (QcType | undefined)[]
        rnaStandardQc?: (QcType | undefined)[]
    }
    const displaySettingForm = useForm<DisplaySettingForm>({ standardQc: ['RT_SPIKE_IN'], rnaStandardQc: ['RT_SPIKE_IN', 'RNA_SPIKE_IN'] })

    useEffect(() => {
        productItemApi.execute()
    }, [])

    useEffect(() => {
        globalState.setLoading(productItemApi.loading)
        if (productItemApi.isSuccess()) {
            versionNumberForm.update((f) => {
                f.qc = productItemApi.response.results.find((r) => r.isStandardQc)?.currentProtocolVersion?.number ?? 0
                f.rnaStandard = productItemApi.response.results.find((r) => r.isRnaStandardQc)?.currentProtocolVersion?.number ?? 0
            })
        }
    }, [productItemApi.loading])

    return (
        <>
            <ContentHeader
                headerTitle="精度管理レポート"
                right={
                    <>
                        <SelectField form={form} attr="year" selectItems={selectYears()} style={{ width: 120 }} />
                        <SelectField
                            form={form}
                            attr="month"
                            selectItems={new Array(12).fill(0).map((y, idx) => {
                                const month = idx + 1
                                return { label: month + '月', value: ('0' + String(month)).slice(-2) }
                            })}
                        />
                    </>
                }
            />
            <ContentBody>
                <Flex flexDirection="column" style={{ width: '95%', margin: 10, marginLeft: 0, height: 'calc(100% - 73px)' }}>
                    <Accordion style={{ width: '100%' }} defaultActiveKey={[`${productItemApi.response.results.find((r) => r.isStandardQc)?.id}`]} alwaysOpen>
                        {productItemApi.response.results.map((productItem) => {
                            let versionNumber: number | undefined = undefined
                            if (productItem.isRnaStandardQc) {
                                versionNumber = versionNumberForm.object.rnaStandard
                            } else if (productItem.isStandardQc) {
                                versionNumber = versionNumberForm.object.qc
                            }
                            return (
                                <Accordion.Item key={`${productItem.id}`} eventKey={`${productItem.id}`}>
                                    <Accordion.Header>{productItem.name?.split('_')[0]}</Accordion.Header>
                                    <Accordion.Body style={{ minHeight: 500 }}>
                                        {versionNumber && (
                                            <>
                                                <Flex style={{ marginBottom: 10 }}>
                                                    <DropdownButton
                                                        variant="none"
                                                        style={{
                                                            backgroundColor:
                                                                protocolVersionViewModle.versionColors[
                                                                    versionNumber % protocolVersionViewModle.versionColors.length
                                                                ],
                                                            borderRadius: 5,
                                                            width: 70,
                                                            marginLeft: 5,
                                                        }}
                                                        as={ButtonGroup}
                                                        id={`dropdown`}
                                                        title={<a style={{ fontWeight: 'bold', marginRight: 3, color: 'white' }}>{`ver. ${versionNumber}`}</a>}
                                                        className=""
                                                        size="sm"
                                                    >
                                                        {productItem.protocolversionSet
                                                            ?.sort((a, b) => (a.number ?? -1) - (b.number ?? -1))
                                                            .map((r) => (
                                                                <Dropdown.Item
                                                                    active={r.number === versionNumber}
                                                                    onClick={() =>
                                                                        versionNumberForm.update((f) => {
                                                                            if (productItem.isStandardQc) {
                                                                                f.qc = r.number!
                                                                            } else if (productItem.isRnaStandardQc) {
                                                                                f.rnaStandard = r.number!
                                                                            }
                                                                        })
                                                                    }
                                                                    key={r.id}
                                                                    eventKey="1"
                                                                >{`ver.${r.number}`}</Dropdown.Item>
                                                            ))}
                                                    </DropdownButton>
                                                    <ModalButton
                                                        openButton={
                                                            <Button size="sm" variant="secondary">
                                                                表示設定
                                                            </Button>
                                                        }
                                                        modalHeader={<div>表示設定</div>}
                                                        modalBody={
                                                            <MultipleSelectField
                                                                id="qc report"
                                                                hideSearch
                                                                form={displaySettingForm}
                                                                attr={productItem.isStandardQc ? 'standardQc' : 'rnaStandardQc'}
                                                                selectItems={QC_TYPE.map((q) => ({ label: q, value: q }))}
                                                                label="QcType"
                                                            />
                                                        }
                                                    />
                                                </Flex>
                                                <Flex style={{ flexWrap: 'wrap' }}>
                                                    {productItem.protocolversionSet
                                                        ?.find((v) => v.number === versionNumber)
                                                        ?.protocolVersionProbes?.sort((a, b) => ((a.probe?.name ?? '') > (b.probe?.name ?? '') ? 1 : -1))
                                                        .filter(
                                                            (p) =>
                                                                displaySettingForm.object[productItem.isStandardQc ? 'standardQc' : 'rnaStandardQc']?.includes(
                                                                    p.qcType
                                                                ) || !QC_TYPE.includes(p.qcType!)
                                                        )
                                                        .map((p, i) => (
                                                            <div key={i} style={{ width: '48%' }}>
                                                                <CtChart
                                                                    probeName={p.probe?.name ?? ''}
                                                                    qcType={p.qcType}
                                                                    ucl={p?.xucl}
                                                                    lcl={p?.xlcl}
                                                                    rucl={p?.rucl}
                                                                    ctChartSearchForm={form.object}
                                                                    dnaCode={productItem.isStandardQc ? 'qPCR_standard' : 'STD'}
                                                                />
                                                            </div>
                                                        ))}
                                                </Flex>
                                            </>
                                        )}
                                    </Accordion.Body>
                                </Accordion.Item>
                            )
                        })}
                    </Accordion>
                </Flex>
            </ContentBody>
        </>
    )
}

export default withRouter(CtChartPage)
